/**
 * Staff Details - Team Health Score Component
 * Calculates and displays team health metrics
 */
import { TrendingUp, Lightbulb } from 'lucide-react';

export const calculateTeamHealthScore = (summary) => {
  const totalStaff = summary?.total_staff || 0;
  const totalWithDetails = summary?.total_with_details || 0;
  const vakdData = summary?.vakd_distribution || {};
  const dopeData = summary?.dope_distribution || {};
  const leadershipData = summary?.leadership_distribution || {};
  
  const totalVakd = Object.values(vakdData).reduce((a, b) => a + b, 0);
  const totalDope = Object.values(dopeData).reduce((a, b) => a + b, 0);
  
  let score = 0;
  const breakdown = [];
  
  // 1. Data Completeness (25 points max)
  const completenessPercent = totalStaff > 0 ? (totalWithDetails / totalStaff) * 100 : 0;
  const completenessScore = Math.min(25, (completenessPercent / 100) * 25);
  score += completenessScore;
  breakdown.push({
    category: 'Data Lengkap',
    score: Math.round(completenessScore),
    max: 25,
    detail: `${totalWithDetails}/${totalStaff} staff (${completenessPercent.toFixed(0)}%)`,
    status: completenessPercent >= 80 ? 'good' : completenessPercent >= 50 ? 'moderate' : 'poor'
  });
  
  // 2. VAKD Diversity (25 points max)
  const vakdTypes = Object.values(vakdData).filter(v => v > 0).length;
  const vakdDiversityScore = totalVakd > 0 ? (vakdTypes / 4) * 25 : 0;
  score += vakdDiversityScore;
  breakdown.push({
    category: 'VAKD Diversity',
    score: Math.round(vakdDiversityScore),
    max: 25,
    detail: `${vakdTypes}/4 jenis learning style`,
    status: vakdTypes >= 3 ? 'good' : vakdTypes >= 2 ? 'moderate' : 'poor'
  });
  
  // 3. DOPE Balance (25 points max)
  const dopeTypes = Object.values(dopeData).filter(v => v > 0).length;
  const dopeDiversityScore = totalDope > 0 ? (dopeTypes / 4) * 25 : 0;
  score += dopeDiversityScore;
  breakdown.push({
    category: 'DOPE Balance',
    score: Math.round(dopeDiversityScore),
    max: 25,
    detail: `${dopeTypes}/4 jenis behavior style`,
    status: dopeTypes >= 3 ? 'good' : dopeTypes >= 2 ? 'moderate' : 'poor'
  });
  
  // 4. Leadership Pipeline (25 points max)
  const highLeaders = leadershipData['High'] || 0;
  const mediumLeaders = leadershipData['Medium'] || 0;
  const idealHighPercent = 0.15;
  const idealMediumPercent = 0.30;
  
  let leadershipScore = 0;
  if (totalWithDetails > 0) {
    const highPercent = highLeaders / totalWithDetails;
    const mediumPercent = mediumLeaders / totalWithDetails;
    
    if (highPercent >= idealHighPercent) {
      leadershipScore += 15;
    } else {
      leadershipScore += (highPercent / idealHighPercent) * 15;
    }
    
    if (mediumPercent >= idealMediumPercent) {
      leadershipScore += 10;
    } else {
      leadershipScore += (mediumPercent / idealMediumPercent) * 10;
    }
  }
  score += leadershipScore;
  breakdown.push({
    category: 'Leadership Pipeline',
    score: Math.round(leadershipScore),
    max: 25,
    detail: `${highLeaders} High, ${mediumLeaders} Medium potential`,
    status: leadershipScore >= 20 ? 'good' : leadershipScore >= 10 ? 'moderate' : 'poor'
  });
  
  return {
    score: Math.round(score),
    maxScore: 100,
    percentage: Math.round((score / 100) * 100),
    breakdown,
    grade: score >= 80 ? 'A' : score >= 60 ? 'B' : score >= 40 ? 'C' : score >= 20 ? 'D' : 'F',
    status: score >= 80 ? 'excellent' : score >= 60 ? 'good' : score >= 40 ? 'moderate' : 'needs_attention'
  };
};

export const getHealthScoreColor = (score) => {
  if (score >= 80) return { bg: 'from-green-500 to-emerald-600', text: 'text-green-600', ring: 'ring-green-500' };
  if (score >= 60) return { bg: 'from-blue-500 to-cyan-600', text: 'text-blue-600', ring: 'ring-blue-500' };
  if (score >= 40) return { bg: 'from-amber-500 to-orange-600', text: 'text-amber-600', ring: 'ring-amber-500' };
  return { bg: 'from-red-500 to-pink-600', text: 'text-red-600', ring: 'ring-red-500' };
};

export const getStatusColor = (status) => {
  if (status === 'good') return 'bg-green-100 text-green-700';
  if (status === 'moderate') return 'bg-amber-100 text-amber-700';
  return 'bg-red-100 text-red-700';
};

export const getImprovementTips = (breakdown) => {
  const tips = [];
  const sortedBreakdown = [...breakdown].sort((a, b) => (a.score/a.max) - (b.score/b.max));
  
  sortedBreakdown.slice(0, 2).forEach(item => {
    if (item.score < item.max * 0.6) {
      if (item.category === 'Data Lengkap') {
        tips.push('📝 Lengkapkan data personaliti untuk lebih banyak staff');
      } else if (item.category === 'VAKD Diversity') {
        tips.push('🧠 Team kurang diverse dari segi learning style');
      } else if (item.category === 'DOPE Balance') {
        tips.push('🎯 Team kurang seimbang dari segi behavior');
      } else if (item.category === 'Leadership Pipeline') {
        tips.push('👑 Perlu develop lebih ramai potential leaders');
      }
    }
  });
  
  return tips;
};

// Team Health Score Display Component
const TeamHealthScoreCard = ({ healthScore, improvementTips }) => {
  const scoreColor = getHealthScoreColor(healthScore.percentage);
  
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
      <div className="grid md:grid-cols-3 gap-0">
        {/* Score Display */}
        <div className={`bg-gradient-to-br ${scoreColor.bg} p-8 flex flex-col items-center justify-center text-white`}>
          <p className="text-sm font-medium opacity-80 mb-2">Team Health Score</p>
          <div className="relative">
            <svg className="w-36 h-36" viewBox="0 0 100 100">
              <circle
                cx="50" cy="50" r="45"
                fill="none"
                stroke="rgba(255,255,255,0.2)"
                strokeWidth="8"
              />
              <circle
                cx="50" cy="50" r="45"
                fill="none"
                stroke="white"
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={`${healthScore.percentage * 2.83} 283`}
                transform="rotate(-90 50 50)"
                className="transition-all duration-1000"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-5xl font-black">{healthScore.percentage}</span>
              <span className="text-lg font-medium opacity-80">/ 100</span>
            </div>
          </div>
          <div className="mt-4 px-4 py-2 bg-white/20 rounded-full">
            <span className="font-bold text-lg">Grade: {healthScore.grade}</span>
          </div>
        </div>
        
        {/* Score Breakdown */}
        <div className="p-6 md:col-span-2">
          <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-indigo-600" />
            Score Breakdown
          </h3>
          <div className="space-y-4">
            {healthScore.breakdown.map((item, idx) => (
              <div key={idx} className="space-y-1">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-700">{item.category}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                      {item.status === 'good' ? 'Baik' : item.status === 'moderate' ? 'Sederhana' : 'Perlu Perhatian'}
                    </span>
                  </div>
                  <span className="text-sm font-bold text-gray-600">{item.score}/{item.max}</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all duration-700 ${
                      item.status === 'good' ? 'bg-green-500' : 
                      item.status === 'moderate' ? 'bg-amber-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${(item.score / item.max) * 100}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500">{item.detail}</p>
              </div>
            ))}
          </div>
          
          {/* Improvement Tips */}
          {improvementTips.length > 0 && (
            <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="font-medium text-amber-800 text-sm mb-2 flex items-center gap-1">
                <Lightbulb className="w-4 h-4" /> Tips untuk tingkatkan score:
              </p>
              <ul className="space-y-1">
                {improvementTips.map((tip, i) => (
                  <li key={i} className="text-sm text-amber-700">{tip}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamHealthScoreCard;
